<script setup lang="ts">
import { watch, toRefs, ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from 'vue-i18n';
import type { EventType, TournamentData, TournamentRound } from "@interfaces/event_type";
import RosterFormatSelect from "./util/RosterFormatSelect.vue";
import EventTypeSelect from "./util/EventTypeSelect.vue";
import AddressInput from "./util/AddressInput.vue";
import AppSelect from "../shared/AppSelect.vue";
import TournamentRounds from "./util/TournamentRounds.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';

interface Props {
  event: EventType;
  city?: string;
}

const props = defineProps<Props>();
const emit = defineEmits(["save", "save-success"]);

const { t } = useI18n();

// Destructure props with reactivity
const { event, city } = toRefs(props);

const COUNTRY_CODES = ['ru', 'by', 'kz', 'ua', 'am', 'az', 'ge', 'kg', 'md', 'tj', 'tm', 'uz'];

const countryOptions = computed(() => {
  const codes = [...COUNTRY_CODES];
  const current = event.value.country?.toString().toLowerCase().trim();
  if (current && current.length === 2 && !codes.includes(current)) {
    codes.push(current);
  }

  return codes.map((code) => {
    const key = `events.form.countries.${code}`;
    const translated = t(key);
    return {
      value: code,
      label: translated === key ? code.toUpperCase() : translated,
    };
  });
});

const onCountryChange = (value: string | number) => {
  event.value.country = String(value || '').toLowerCase();
  handleInputChange('country');
};

// DatePicker refs
const startDatePicker = ref();
const endDatePicker = ref();

// Save state management
let isSaving = Boolean(false);
const hasChanges = ref(!event.value?.id); // true for new events (no ID), false for existing ones (has ID)
const hasAvatarChanges = ref(false);

// Error handling
const errors = ref<Record<string, string[]>>({});
const generalError = ref<string>('');

// Simple watcher for changes - similar to SelectStructure.vue approach
watch(
  () => event.value,
  () => {
    hasChanges.value = true;
    // Clear errors when user makes changes
    clearErrors();
  },
  { deep: true }
);

// Debug watcher for tournament data
watch(
  () => event.value.data,
  () => {},
  { deep: true }
);

// Watcher to validate end date when start date changes
watch(
  () => event.value.starts_at,
  (newStartDate) => {
    if (newStartDate && event.value.ends_at) {
      const startDate = newStartDate instanceof Date ? newStartDate : new Date(newStartDate);
      const endDate = event.value.ends_at instanceof Date ? event.value.ends_at : new Date(event.value.ends_at);

      // If end date is now before or equal to start date, clear it
      if (endDate <= startDate) {
        event.value.ends_at = undefined;
      }
    }
  }
);

// Computed property to check if there are any changes (including avatar)
const hasAnyChanges = computed(() => {
  return hasChanges.value || hasAvatarChanges.value;
});

// Watch for city changes and update event.city if needed
watch(
  () => city?.value,
  (newCity) => {
    if (newCity && !event.value.city) {
      event.value.city = newCity;
    }
  }
);

// Computed property for address with fallback
const addressValue = computed({
  get: () => event.value.address || '',
  set: (value: string) => {
    event.value.address = value;
  }
});

// Computed property for end date minimum date (start date + 1 minute)
const endDateMinDate = computed(() => {
  if (!event.value.starts_at) return undefined;

  // If starts_at is a Date object, add 1 minute to it
  if (event.value.starts_at instanceof Date) {
    const minDate = new Date(event.value.starts_at);
    minDate.setMinutes(minDate.getMinutes() + 1);
    return minDate;
  }

  // If starts_at is a string, parse it and add 1 minute
  if (typeof event.value.starts_at === 'string') {
    const startDate = new Date(event.value.starts_at);
    if (!isNaN(startDate.getTime())) {
      const minDate = new Date(startDate);
      minDate.setMinutes(minDate.getMinutes() + 1);
      return minDate;
    }
  }

  return undefined;
});

// Computed property to check if tournament is selected
const isTournament = computed(() => {
  return event.value.event_type === 'tournament' || event.value.event_type === '3' || event.value.event_type === 3;
});

// Computed property for tournament data
const tournamentData = computed((): TournamentData => {
  if (isTournament.value) {
    // Ensure event.data exists and is an object
    if (!event.value.data || typeof event.value.data !== 'object') {
      event.value.data = { rounds: [], required_rosters_count: 1 };
    }

    // Check if we have rounds data, if not initialize it
    if (!('rounds' in event.value.data)) {
      (event.value.data as any).rounds = [];
    }

    // Ensure required_rosters_count exists
    if (!('required_rosters_count' in event.value.data)) {
      (event.value.data as any).required_rosters_count = 1;
    }

    return event.value.data as TournamentData;
  }

  return { rounds: [] };
});

// Computed property for required rosters count (tournament only)
const requiredRostersCount = computed({
  get: () => {
    if (!isTournament.value) return 1;
    if (event.value.data && typeof event.value.data === 'object' && 'required_rosters_count' in event.value.data) {
      return Number((event.value.data as any).required_rosters_count) || 1;
    }
    return 1;
  },
  set: (value: number) => {
    if (!event.value.data || typeof event.value.data !== 'object') {
      event.value.data = { rounds: [], required_rosters_count: value };
    } else {
      // Preserve existing data, just update the required_rosters_count
      (event.value.data as any).required_rosters_count = value;
    }
  }
});

// Method to handle tournament rounds updates
const handleTournamentRoundsUpdate = (rounds: TournamentRound[]) => {
  if (isTournament.value) {
    if (!event.value.data) {
      event.value.data = { rounds: rounds, required_rosters_count: 1 };
    } else if (typeof event.value.data === 'object') {
      // Preserve existing data, just update the rounds
      (event.value.data as any).rounds = rounds;
    }
  }

  // Clear tournament rounds error when user updates rounds
  if (hasFieldError('tournament_rounds')) {
    errors.value.tournament_rounds = [];
  }
};

// Computed property for save button text
const saveButtonText = computed(() => {
  if (isSaving) return t('events.form.saving');
  return hasAnyChanges.value ? t('events.form.save') : t('events.form.saved');
});

// Error handling functions
const clearErrors = () => {
  errors.value = {};
  generalError.value = '';
};

const setFieldError = (field: string, message: string) => {
  if (!errors.value[field]) {
    errors.value[field] = [];
  }
  errors.value[field].push(message);
};

const hasFieldError = (field: string): boolean => {
  return errors.value[field] && errors.value[field].length > 0;
};

const getFieldError = (field: string): string => {
  return errors.value[field] ? errors.value[field][0] : '';
};

// Frontend validation
const validateForm = (): boolean => {
  clearErrors();
  let isValid = true;

  // Required field validations
  if (!event.value.name?.trim()) {
    setFieldError('name', t('events.form.errors.name_required'));
    isValid = false;
  }

  if (!event.value.event_type) {
    setFieldError('event_type', t('events.form.errors.event_type_required'));
    isValid = false;
  }

  if (!event.value.address?.trim()) {
    setFieldError('address', t('events.form.errors.address_required'));
    isValid = false;
  }

  if (!event.value.starts_at) {
    setFieldError('starts_at', t('events.form.errors.starts_at_required'));
    isValid = false;
  }

  if (!event.value.ends_at) {
    setFieldError('ends_at', t('events.form.errors.ends_at_required'));
    isValid = false;
  }

  if (event.value.starts_at && event.value.ends_at) {
    const startDate = new Date(event.value.starts_at);
    const endDate = new Date(event.value.ends_at);
    if (startDate >= endDate) {
      setFieldError('ends_at', t('events.form.errors.ends_at_after_starts_at'));
      isValid = false;
    }
  }

  if (!event.value.roster_format_id) {
    setFieldError('roster_format_id', t('events.form.errors.roster_format_required'));
    isValid = false;
  }

  if (!event.value.country?.trim()) {
    setFieldError('country', t('events.form.errors.country_required'));
    isValid = false;
  }

  if (!event.value.city?.trim()) {
    setFieldError('city', t('events.form.errors.city_required'));
    isValid = false;
  }

  if (
    event.value.max_players === undefined ||
    event.value.max_players === null ||
    event.value.max_players === 0 ||
    String(event.value.max_players).trim() === ''
  ) {
    setFieldError('max_players', t('events.form.errors.max_players_required'));
    isValid = false;
  }

  // Numeric validations
  if (event.value.max_players !== undefined && event.value.max_players !== null && event.value.max_players !== 0 && String(event.value.max_players).trim() !== '') {
    if (!Number.isInteger(event.value.max_players) || event.value.max_players < 0) {
      setFieldError('max_players', t('events.form.errors.max_players_positive_integer'));
      isValid = false;
    }
    if (event.value.max_players > 1000) {
      setFieldError('max_players', t('events.form.errors.max_players_max_1000'));
      isValid = false;
    }
  }

  if (event.value.price !== undefined && event.value.price !== null) {
    if (!Number.isInteger(event.value.price) || event.value.price < 0) {
      setFieldError('price', t('events.form.errors.price_positive_integer'));
      isValid = false;
    }
  }

            // Tournament-specific validations
    if (isTournament.value) {
      const tournamentRounds = tournamentData.value.rounds;

      if (!tournamentRounds || tournamentRounds.length === 0) {
        setFieldError('tournament_rounds', t('events.form.errors.tournament_rounds_required'));
        isValid = false;
      } else {
        // Validate each round
        tournamentRounds.forEach((round, index) => {
          if (!round.scenario_id) {
            setFieldError('tournament_rounds', t('events.form.errors.round_scenario_required', { round: index + 1 }));
            isValid = false;
          }
          // Map selection is now optional
        });
      }
      const count = requiredRostersCount.value;
      if (count !== 1 && count !== 2) {
        setFieldError('required_rosters_count', t('events.form.errors.required_rosters_count_invalid'));
        isValid = false;
      }
    }

  return isValid;
};

const handleSave = async () => {
  if (isSaving) return;

  // Frontend validation
  if (!validateForm()) {
    return;
  }

  isSaving = true;
  clearErrors();

  // Check if we have avatar changes
  const fileInput = document.getElementById('event-avatar-input') as HTMLInputElement;
  const avatarFile = fileInput?.files?.[0];

  // Prepare form data
  const formData = new FormData();

  // Add all event data to form data
  const startsAt = event.value.starts_at instanceof Date ? event.value.starts_at.toISOString() : event.value.starts_at;
  const endsAt = event.value.ends_at instanceof Date ? event.value.ends_at.toISOString() : event.value.ends_at;

  formData.append('starts_at', startsAt || '');
  formData.append('ends_at', endsAt || '');
  formData.append('roster_format_id', event.value.roster_format_id?.toString() || '');
  formData.append('event_type', event.value.event_type?.toString() || '');
  formData.append('country', event.value.country || '');
  formData.append('city', event.value.city || '');
  formData.append('address', event.value.address || '');
  formData.append('max_players', event.value.max_players?.toString() || '');
  formData.append('social_links', event.value.social_links || '');
  formData.append('price', event.value.price?.toString() || '0');

  // Add optional fields
  if (event.value.name) formData.append('name', event.value.name);
  if (event.value.description) formData.append('description', event.value.description);
  if (event.value.latitude) formData.append('latitude', event.value.latitude.toString());
  if (event.value.longitude) formData.append('longitude', event.value.longitude.toString());

  // Add tournament data if it's a tournament
  if (isTournament.value && event.value.data) {
    // Create a copy of the data to avoid modifying the original
    const tournamentData = { ...event.value.data };
    tournamentData.required_rosters_count = requiredRostersCount.value;
    formData.append('data', JSON.stringify(tournamentData));
  } else if (isTournament.value) {
    const defaultTournamentData = { required_rosters_count: requiredRostersCount.value, rounds: [] };
    formData.append('data', JSON.stringify(defaultTournamentData));
  }

  // Add avatar file if present
  if (avatarFile) {
    formData.append('avatar', avatarFile);
  }

  emit("save", formData, (response: any) => {
    isSaving = false;

    // If save was successful
    if (response && (response.status === 200 || response.status === 201)) {
      hasChanges.value = false;
      hasAvatarChanges.value = false;
      // Emit success event for parent component to handle navigation
      emit("save-success", response.data);
    }
  }, (error: any) => {
    isSaving = false;

    // Handle backend validation errors
    if (error.response && error.response.status === 422) {
      const errorData = error.response.data;

      if (errorData.field_errors) {
        // Map backend field errors to frontend field names
        Object.keys(errorData.field_errors).forEach(backendField => {
          const frontendField = mapBackendFieldToFrontend(backendField);
          errorData.field_errors[backendField].forEach((message: string) => {
            setFieldError(frontendField, message);
          });
        });
      }

      if (errorData.errors && errorData.errors.length > 0) {
        generalError.value = errorData.errors.join(', ');
      }
    } else if (error.response && error.response.status === 400) {
      const errorData = error.response.data;
      let message = '';
      if (typeof errorData === 'string') {
        try {
          const parsed = JSON.parse(errorData);
          message = parsed.error_msg || parsed.error || '';
        } catch {
          message = errorData;
        }
      } else {
        message = errorData?.error_msg || errorData?.error || '';
      }
      generalError.value = message || t('events.form.errors.save_error');
    } else if (error.response && error.response.status === 403) {
      generalError.value = t('events.form.errors.forbidden');
    } else {
      // Handle other errors
      generalError.value = t('events.form.errors.save_error');
    }
  });
};

// Map backend field names to frontend field names
const mapBackendFieldToFrontend = (backendField: string): string => {
  const fieldMap: Record<string, string> = {
    'duration': 'starts_at',
    'roster_format': 'roster_format_id',
    'event_type': 'event_type',
    'max_players': 'max_players',
    'social_links': 'social_links'
  };

  return fieldMap[backendField] || backendField;
};

const openStartDatePicker = () => {
  if (startDatePicker.value) {
    startDatePicker.value.toggleMenu();
  }
};

const openEndDatePicker = () => {
  if (endDatePicker.value) {
    endDatePicker.value.toggleMenu();
  }
};

const handleLocationUpdate = (location: {
  latitude: number;
  longitude: number;
  country: string;
  city: string;
}) => {
  // Update event with location data
  event.value.latitude = location.latitude;
  event.value.longitude = location.longitude;
  if (location.country) event.value.country = location.country.toLowerCase();
  if (location.city) event.value.city = location.city;
};

// Validation functions for number inputs
const validatePositiveInteger = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  let numValue = parseInt(value) || 0;

  // Ensure the value is non-negative
  if (numValue < 0) {
    numValue = 0;
  }

  return numValue;
};

const handleMaxPlayersChange = (formEvent: Event) => {
  const target = formEvent.target as HTMLInputElement;
  const validatedValue = validatePositiveInteger(target.value);
  event.value.max_players = validatedValue;
  target.value = validatedValue.toString();

  // Clear field error when user fixes the value
  if (hasFieldError('max_players')) {
    errors.value.max_players = [];
  }
};

const handlePriceChange = (formEvent: Event) => {
  const target = formEvent.target as HTMLInputElement;
  const validatedValue = validatePositiveInteger(target.value);
  event.value.price = validatedValue;
  target.value = validatedValue.toString();

  // Clear field error when user fixes the value
  if (hasFieldError('price')) {
    errors.value.price = [];
  }
};

const handleNumberKeyDown = (event: KeyboardEvent) => {
  // Allow: backspace, delete, tab, escape, enter, and navigation keys
  if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(event.keyCode)) {
    return;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode)) {
    return;
  }

  // Allow: numbers and minus sign
  if ((event.keyCode >= 48 && event.keyCode <= 57) || event.keyCode === 45) {
    // If minus sign, only allow it at the beginning
    if (event.keyCode === 45) {
      const target = event.target as HTMLInputElement;
      if (target.selectionStart !== 0) {
        event.preventDefault();
        return;
      }
    }
    return;
  }

  // Prevent all other keys
  event.preventDefault();
};

const handleNumberPaste = (pasteEvent: ClipboardEvent) => {
  pasteEvent.preventDefault();
  const pastedText = pasteEvent.clipboardData?.getData('text/plain') || '';

  // Only allow digits and minus sign
  const cleanText = pastedText.replace(/[^\d-]/g, '');

  if (cleanText) {
    const target = pasteEvent.target as HTMLInputElement;
    const currentValue = target.value;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;

    // Insert the cleaned text at cursor position
    const newValue = currentValue.substring(0, selectionStart) + cleanText + currentValue.substring(selectionEnd);

    // Validate the new value
    let numValue = parseInt(newValue) || 0;
    if (numValue < 0) {
      numValue = 0;
    }

    target.value = numValue.toString();

    // Update the appropriate field
    if (target.classList.contains('max-players-input')) {
      event.value.max_players = numValue;
    } else if (target.classList.contains('price-input')) {
      event.value.price = numValue;
    }
  }
};

// Avatar handling
const handleAvatarChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    // Set avatar changes flag
    hasAvatarChanges.value = true;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('events.form.errors.invalid_image_format'));
      target.value = '';
      hasAvatarChanges.value = false;
      resetFileInputText(target);
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      alert(t('events.form.errors.file_too_large', { fileSize: fileSizeMB, maxSize: maxSizeMB }));
      target.value = '';
      hasAvatarChanges.value = false;
      resetFileInputText(target);
      return;
    }

    // Update file input text with selected file name
    updateFileInputText(target, file.name);

    // Show crop modal for event avatar
    showEventCropModal(file);
  } else {
    // Reset text if no file selected
    resetFileInputText(target);
  }
};

// Update file input text with selected file name
const updateFileInputText = (fileInput: HTMLInputElement, fileName: string) => {
  const label = fileInput.closest('.file-input-wrapper')?.querySelector('.file-input-label') as HTMLLabelElement;
  if (!label) return;

  const textElement = label.querySelector('.file-input-text') as HTMLElement;
  if (!textElement) return;

  // Get translation from data attribute
  const selectedFileText = label.getAttribute('data-selected-file-text') || t('events.form.selected_file');

  // Update text with prefix and file name
  textElement.setAttribute('data-selected-prefix', selectedFileText);
  textElement.textContent = selectedFileText + fileName;
};

// Reset file input text
const resetFileInputText = (fileInput: HTMLInputElement) => {
  const label = fileInput.closest('.file-input-wrapper')?.querySelector('.file-input-label') as HTMLLabelElement;
  if (label) {
    const textElement = label.querySelector('.file-input-text') as HTMLElement;
    if (textElement) {
      const selectFileText = label.getAttribute('data-select-file-text') || t('events.form.select_file');
      textElement.removeAttribute('data-selected-prefix');
      textElement.textContent = selectFileText;
    }
  }
};

// Event avatar crop functions (reusing existing logic with event-specific IDs)
const showEventCropModal = (file: File) => {
  const modal = document.getElementById('event-crop-modal');
  const cropArea = document.getElementById('event-crop-area');

  if (!modal || !cropArea) return;

  // Clear previous content
  cropArea.innerHTML = '';

  // Create image element
  const img = document.createElement('img');
  img.src = URL.createObjectURL(file);
  img.style.maxWidth = '600px';
  img.style.maxHeight = '400px';
  img.style.width = 'auto';
  img.style.height = 'auto';

  // Add image to crop area
  cropArea.appendChild(img);

  // Show modal
  modal.style.display = 'flex';

  // Initialize cropper after image loads
  img.onload = function() {
    if (window.cropper) {
      window.cropper.destroy();
    }

    if (typeof Cropper === 'undefined') return;

    try {
      window.cropper = new Cropper(img, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        restore: false,
        guides: false,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        background: false,
        modal: true,
        zoomable: true,
        scalable: true,
        rotatable: true,
        checkCrossOrigin: false,
        crop(event: any) {
          // Apply hexagon mask to the crop area
          const cropBox = event.detail;
          const canvas = event.detail.canvas;

          // Check if canvas exists before trying to use it
          if (!canvas) {
            return;
          }

          // Create hexagon path that matches the clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)
          const width = cropBox.width;
          const height = cropBox.height;

          const hexagonPath = new Path2D();
          // Top point (50% 0%)
          hexagonPath.moveTo(width * 0.5, height * 0);
          // Top right (100% 25%)
          hexagonPath.lineTo(width * 1, height * 0.25);
          // Bottom right (100% 75%)
          hexagonPath.lineTo(width * 1, height * 0.75);
          // Bottom point (50% 100%)
          hexagonPath.lineTo(width * 0.5, height * 1);
          // Bottom left (0% 75%)
          hexagonPath.lineTo(width * 0, height * 0.75);
          // Top left (0% 25%)
          hexagonPath.lineTo(width * 0, height * 0.25);
          hexagonPath.closePath();

          // Apply hexagon mask
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.save();
            ctx.clip(hexagonPath);
            ctx.drawImage(canvas, 0, 0);
            ctx.restore();
          }
        }
      });
    } catch (error) {
      console.error('Error initializing cropper:', error);
    }
  };
};

// Make functions globally available for the crop modal
declare global {
  interface Window {
    cropper: any;
    closeEventCropModal: () => void;
    cropEventImage: () => void;
    rotateEventLeft: () => void;
    rotateEventRight: () => void;
  }
}

declare const Cropper: any;

// Input change handlers to clear field errors
const handleInputChange = (field: string) => {
  if (hasFieldError(field)) {
    errors.value[field] = [];
  }
};

const setLink = () => {
  if (!editor.value) return;

  const previousUrl = editor.value.getAttributes('link').href as string | undefined;
  const url = window.prompt(t('events.form.toolbar.link_prompt'), previousUrl || '');

  if (url === null) return;

  if (url.trim() === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }

  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
};

// Tiptap editor setup
const editor = useEditor({
  content: event.value.description || '',
  extensions: [
    StarterKit.configure({
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    }),
  ],
  onUpdate: ({ editor }) => {
    event.value.description = editor.getHTML();
  },
  editorProps: {
    attributes: {
      class: 'tiptap-content',
      'data-placeholder': t('events.form.description_placeholder'),
    },
  },
  autofocus: false,
  editable: true,
});

// Watch for external changes to description (e.g., when loading event)
watch(
  () => event.value.description,
  (newDescription) => {
    if (editor.value && editor.value.getHTML() !== newDescription) {
      editor.value.commands.setContent(newDescription || '');
    }
  }
);

// Cleanup editor on unmount
onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});

// Initialize global functions for crop modal and editor
onMounted(() => {
  // Initialize editor content if needed
  if (editor.value && event.value.description) {
    const currentContent = editor.value.getHTML();
    if (currentContent !== event.value.description) {
      editor.value.commands.setContent(event.value.description || '');
    }
  }

  window.closeEventCropModal = () => {
    const modal = document.getElementById('event-crop-modal');
    modal!.style.display = 'none';

    if (window.cropper) {
      window.cropper.destroy();
      window.cropper = null;
    }
  };

  window.cropEventImage = () => {
    if (!window.cropper) return;

    const canvas = window.cropper.getCroppedCanvas({
      width: 200,
      height: 200,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    // Apply hexagon mask to the cropped image
    const hexCanvas = document.createElement('canvas');
    hexCanvas.width = 200;
    hexCanvas.height = 200;
    const ctx = hexCanvas.getContext('2d');

    if (ctx) {
      // Create hexagon path that matches the clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)
      const width = 200;
      const height = 200;

      ctx.beginPath();
      // Top point (50% 0%)
      ctx.moveTo(width * 0.5, height * 0);
      // Top right (100% 25%)
      ctx.lineTo(width * 1, height * 0.25);
      // Bottom right (100% 75%)
      ctx.lineTo(width * 1, height * 0.75);
      // Bottom point (50% 100%)
      ctx.lineTo(width * 0.5, height * 1);
      // Bottom left (0% 75%)
      ctx.lineTo(width * 0, height * 0.75);
      // Top left (0% 25%)
      ctx.lineTo(width * 0, height * 0.25);
      ctx.closePath();

      // Apply hexagon mask
      ctx.save();
      ctx.clip();
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();
    }

    // Convert hexagon canvas to blob
    hexCanvas.toBlob(function(blob: Blob | null) {
      if (!blob) return;

      // Create a new file from the blob
      const croppedFile = new File([blob], 'event_avatar.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Update file input
      const fileInput = document.getElementById('event-avatar-input') as HTMLInputElement;
      if (fileInput) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);
        fileInput.files = dataTransfer.files;
      }

      // Update preview
      const preview = document.getElementById('event-avatar-preview');
      if (preview) {
        preview.innerHTML = '';
        const img = document.createElement('img');
        img.src = hexCanvas.toDataURL();
        img.alt = t('events.form.avatar_preview');
        img.style.width = '36px';
        img.style.height = '41px';
        img.style.borderRadius = '0';
        img.style.border = 'none';
        img.style.objectFit = 'cover';
        img.style.position = 'relative';
        img.style.zIndex = '2';
        img.style.clipPath = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';
        preview.appendChild(img);
      }

      // Set avatar changes flag
      hasAvatarChanges.value = true;

      // Close modal
      window.closeEventCropModal();
    }, 'image/jpeg');
  };

  window.rotateEventLeft = () => {
    if (window.cropper) {
      window.cropper.rotate(-90);
    }
  };

  window.rotateEventRight = () => {
    if (window.cropper) {
      window.cropper.rotate(90);
    }
  };
});
</script>

<template>
  <div class="events__form mb-2 container-fluid">
    <div class="row">
      <div class="col-md-6">
        <form autocomplete="off" data-1p-ignore>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.name') }}:
              <input
                class="form-control"
                :class="{ 'is-invalid': hasFieldError('name') }"
                v-model="event.name"
                :placeholder="t('events.form.placeholders.enter_manually')"
                autocomplete="off"
                data-1p-ignore
                @input="handleInputChange('name')"
              />
            </label>
            <div v-if="hasFieldError('name')" class="text-danger error-message">
              {{ getFieldError('name') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.event_type') }}:
              <event-type-select
                v-model="event.event_type"
                :custom-class="{ 'is-invalid': hasFieldError('event_type') }"
                @change="handleInputChange('event_type')"
              />
            </label>
            <div v-if="hasFieldError('event_type')" class="text-danger error-message">
              {{ getFieldError('event_type') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.address') }}:
              <AddressInput
                v-model="addressValue"
                :class="{ 'is-invalid': hasFieldError('address') }"
                @location-update="handleLocationUpdate"
                @input="handleInputChange('address')"
                :placeholder="t('events.form.placeholders.enter_event_address')"
              />
            </label>
            <div v-if="hasFieldError('address')" class="text-danger error-message">
              {{ getFieldError('address') }}
            </div>
            <div class="text-muted small">
              {{ t('events.form.hints.address_location') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.country') }}:
              <AppSelect
                variant="events-form"
                :class="{ 'is-invalid': hasFieldError('country') }"
                :model-value="event.country || ''"
                :options="countryOptions"
                :placeholder="t('events.form.placeholders.select_country')"
                @update:model-value="onCountryChange"
                @change="onCountryChange"
              />
            </label>
            <div v-if="hasFieldError('country')" class="text-danger error-message">
              {{ getFieldError('country') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.city') }}:
              <input
                class="form-control"
                :class="{ 'is-invalid': hasFieldError('city') }"
                v-model="event.city"
                :placeholder="t('events.form.placeholders.enter_city')"
                autocomplete="off"
                data-1p-ignore
                @input="handleInputChange('city')"
              />
            </label>
            <div v-if="hasFieldError('city')" class="text-danger error-message">
              {{ getFieldError('city') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.date_time') }}:
              <VueDatePicker
                ref="startDatePicker"
                v-model="event.starts_at"
                :class="{ 'is-invalid': hasFieldError('starts_at') }"
                @update:model-value="handleInputChange('starts_at')"
                :placeholder="t('events.form.placeholders.start_date_time')"
                auto-apply
                :enable-time-picker="true"
                format="dd.MM.yyyy HH:mm"
              >
                <template #input-icon>
                  <i class="bi bi-calendar4-week" @click="openStartDatePicker"></i>
                </template>
              </VueDatePicker>
            </label>
            <div v-if="hasFieldError('starts_at')" class="text-danger error-message">
              {{ getFieldError('starts_at') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              <VueDatePicker
                ref="endDatePicker"
                v-model="event.ends_at"
                :class="{ 'is-invalid': hasFieldError('ends_at') }"
                @update:model-value="handleInputChange('ends_at')"
                :placeholder="t('events.form.placeholders.end_date_time')"
                auto-apply
                :enable-time-picker="true"
                format="dd.MM.yyyy HH:mm"
                :min-date="endDateMinDate"
              >
                <template #input-icon>
                  <i class="bi bi-calendar4-week" @click="openEndDatePicker"></i>
                </template>
              </VueDatePicker>
            </label>
            <div v-if="hasFieldError('ends_at')" class="text-danger error-message">
              {{ getFieldError('ends_at') }}
            </div>
            <div v-if="event.starts_at && !hasFieldError('ends_at')" class="text-muted small">
              {{ t('events.form.hints.end_date_after_start') }}
            </div>
          </div>

          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.max_players') }}
              <input
                class="form-control max-players-input"
                :class="{ 'is-invalid': hasFieldError('max_players') }"
                v-model="event.max_players"
                type="number"
                min="0"
                step="1"
                :placeholder="t('events.form.placeholders.enter_manually')"
                autocomplete="off"
                data-1p-ignore
                @input="handleMaxPlayersChange"
                @keydown="handleNumberKeyDown"
                @paste="handleNumberPaste"
                :title="t('events.form.hints.positive_integer_or_zero')"
              />
            </label>
            <div v-if="hasFieldError('max_players')" class="text-danger error-message">
              {{ getFieldError('max_players') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.roster_format') }}
              <roster-format-select
                v-model="event.roster_format_id"
                :custom-class="{ 'is-invalid': hasFieldError('roster_format_id') }"
                @change="handleInputChange('roster_format_id')"
              ></roster-format-select>
            </label>
            <div v-if="hasFieldError('roster_format_id')" class="text-danger error-message">
              {{ getFieldError('roster_format_id') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.price') }}
              <input
                class="form-control price-input"
                :class="{ 'is-invalid': hasFieldError('price') }"
                v-model="event.price"
                type="number"
                min="0"
                step="1"
                :placeholder="t('events.form.placeholders.enter_manually')"
                autocomplete="off"
                data-1p-ignore
                @input="handlePriceChange"
                @keydown="handleNumberKeyDown"
                @paste="handleNumberPaste"
                :title="t('events.form.hints.positive_integer_or_zero')"
              />
            </label>
            <div v-if="hasFieldError('price')" class="text-danger error-message">
              {{ getFieldError('price') }}
            </div>
          </div>
          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.social_links') }}
              <input
                class="form-control"
                :class="{ 'is-invalid': hasFieldError('social_links') }"
                v-model="event.social_links"
                :placeholder="t('events.form.placeholders.enter_manually')"
                autocomplete="off"
                data-1p-ignore
                @input="handleInputChange('social_links')"
              />
            </label>
            <div v-if="hasFieldError('social_links')" class="text-danger error-message">
              {{ getFieldError('social_links') }}
            </div>
          </div>

          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.description') }}:
              <div class="tiptap-editor-wrapper" v-if="editor">
                <!-- Toolbar -->
                <div class="tiptap-toolbar" v-if="editor">
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleBold().run()"
                    :class="{ 'is-active': editor.isActive('bold') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.bold')"
                  >
                    <i class="bi bi-type-bold"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleItalic().run()"
                    :class="{ 'is-active': editor.isActive('italic') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.italic')"
                  >
                    <i class="bi bi-type-italic"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleStrike().run()"
                    :class="{ 'is-active': editor.isActive('strike') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.strike')"
                  >
                    <i class="bi bi-type-strikethrough"></i>
                  </button>
                  <button
                    type="button"
                    @click="setLink"
                    :class="{ 'is-active': editor.isActive('link') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.link')"
                  >
                    <i class="bi bi-link-45deg"></i>
                  </button>
                  <div class="toolbar-divider"></div>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.heading_1')"
                  >
                    <i class="bi bi-type-h1"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.heading_2')"
                  >
                    <i class="bi bi-type-h2"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.heading_3')"
                  >
                    <i class="bi bi-type-h3"></i>
                  </button>
                  <div class="toolbar-divider"></div>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleBulletList().run()"
                    :class="{ 'is-active': editor.isActive('bulletList') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.bullet_list')"
                  >
                    <i class="bi bi-list-ul"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleOrderedList().run()"
                    :class="{ 'is-active': editor.isActive('orderedList') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.ordered_list')"
                  >
                    <i class="bi bi-list-ol"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleBlockquote().run()"
                    :class="{ 'is-active': editor.isActive('blockquote') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.blockquote')"
                  >
                    <i class="bi bi-quote"></i>
                  </button>
                  <div class="toolbar-divider"></div>
                  <button
                    type="button"
                    @click="editor.chain().focus().toggleCode().run()"
                    :class="{ 'is-active': editor.isActive('code') }"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.code')"
                  >
                    <i class="bi bi-code"></i>
                  </button>
                  <button
                    type="button"
                    @click="editor.chain().focus().setHorizontalRule().run()"
                    class="toolbar-button"
                    :title="t('events.form.toolbar.horizontal_rule')"
                  >
                    <i class="bi bi-dash-lg"></i>
                  </button>
                </div>
                <!-- Editor Content -->
                <div class="tiptap-editor-content">
                  <EditorContent :editor="editor" />
                </div>
              </div>
            </label>
          </div>

          <div class="input-group mb-1">
            <label class="form-title">
              {{ t('events.form.fields.avatar') }}:
              <div class="avatar-input-row">
                <div class="file-input-wrapper">
                  <input
                    type="file"
                    id="event-avatar-input"
                    class="form-control file-input"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    data-preview="event-avatar-preview"
                    @change="handleAvatarChange"
                  />
                  <label for="event-avatar-input" class="file-input-label" :data-select-file-text="t('events.form.select_file')" :data-selected-file-text="t('events.form.selected_file')">
                    <span class="file-input-text">{{ t('events.form.select_file') }}</span>
                    <span class="file-input-icon">📁</span>
                  </label>
                </div>
                <div class="current-avatar-preview" id="event-avatar-preview">
                  <img
                    v-if="event.avatar_url"
                    :src="event.avatar_url"
                    :alt="t('events.form.event_avatar')"
                    class="avatar-preview-image"
                  />
                  <img
                    v-else
                    src="@//images/event_default_avatar.jpg"
                    :alt="t('events.form.event_avatar_default')"
                    class="avatar-preview-image"
                  />
                </div>
              </div>
            </label>
          </div>

          <!-- Image crop modal for event avatar -->
          <div id="event-crop-modal" class="crop-modal" style="display: none;">
            <div class="crop-modal-content">
              <div class="crop-modal-header">
                <h3>{{ t('events.form.crop_image') }}</h3>
                <button type="button" class="crop-close-btn" onclick="closeEventCropModal()">×</button>
              </div>
              <div class="crop-container">
                <div id="event-crop-area"></div>
              </div>
              <div class="crop-controls">
                <div class="crop-rotation-controls">
                  <button type="button" class="button button_plain" onclick="rotateEventLeft()" :title="t('events.form.rotate_left')">
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                  <button type="button" class="button button_plain" onclick="rotateEventRight()" :title="t('events.form.rotate_right')">
                    <i class="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
                <div class="crop-action-controls">
                  <button type="button" class="button" onclick="cropEventImage()">{{ t('events.form.crop') }}</button>
                  <button type="button" class="button button_plain" onclick="closeEventCropModal()">{{ t('events.form.cancel') }}</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tournament Rounds Section -->
          <div v-if="isTournament" class="tournament-section">
            <div class="input-group mb-1">
              <label class="form-title">
                {{ t('events.form.fields.required_rosters_count') }}:
                <select
                  v-model.number="requiredRostersCount"
                  class="form-control"
                  :class="{ 'is-invalid': hasFieldError('required_rosters_count') }"
                >
                  <option :value="1">1</option>
                  <option :value="2">2</option>
                </select>
              </label>
              <div v-if="hasFieldError('required_rosters_count')" class="text-danger error-message">
                {{ getFieldError('required_rosters_count') }}
              </div>
            </div>
            <TournamentRounds
              :model-value="tournamentData.rounds"
              @update="handleTournamentRoundsUpdate"
            />
            <div v-if="hasFieldError('tournament_rounds')" class="text-danger error-message">
              {{ getFieldError('tournament_rounds') }}
            </div>
          </div>

          <button
            @click.prevent="handleSave"
            class="button button_wide mb-1"
            :class="{
              'button--saving': isSaving
            }"
            :disabled="isSaving || !hasAnyChanges"
            data-turbolinks="false"
          >
            {{ saveButtonText }}
          </button>
          <div v-if="generalError" class="text-danger error-message general-error">
            {{ generalError }}
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// Color Variables - Using CSS Variables
$primary-bg: var(--primary-bg);
$primary-text: var(--primary-text);
$icon-bg: var(--icon-bg);
$icon-text: var(--icon-text);
$hover-bg: var(--hover-bg);
$border-color: var(--border-color);
$focus-shadow: rgba(189, 156, 101, 0.2);

label.form-title {
  color: var(--text-primary);
}

// Mixins
@mixin form-input-base {
  width: 100%;
  background-color: $primary-bg !important;
  color: $primary-text !important;
  border: 1px solid $border-color;
  border-radius: 4px;
  font-size: 14px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

@mixin focus-state {
  outline: none;
  border-color: $border-color;
  box-shadow: 0 0 0 2px $focus-shadow;
}

@mixin hover-transition {
  transition: all 0.2s ease;
}

.events__form {
  form .form-control {
    @include form-input-base;
  }
}

/* VueDatePicker Custom Styling - Global Override */
.dp__input {
  @include form-input-base;
}

/* Bootstrap Icons styling for VueDatePicker */
.dp__input_icon {
  color: $icon-text !important;
  padding-left: 12px !important;
  cursor: pointer !important;
  @include hover-transition;

  &:hover {
    color: $hover-bg !important;
  }

  .bi {
    font-size: 16px !important;
    color: inherit !important;
  }
}

.dp__input {
  &:focus {
    @include focus-state;
  }

  &::placeholder {
    color: $primary-text !important;
    opacity: 0.7 !important;
  }
}

/* Calendar Popup Styling */
.dp__main {
  background-color: $primary-bg !important;
  border: 1px solid $border-color !important;
  border-radius: 4px !important;
  z-index: auto !important;
}

.dp__menu.dp__menu_index {
  background-color: $primary-bg !important;
  border: 1px solid $border-color !important;
  border-radius: 4px !important;
  z-index: auto !important;
}

/* Remove z-index from all VueDatePicker elements */
.dp__outer_menu {
  z-index: auto !important;
}

.dp__menu {
  z-index: auto !important;
}

.dp__menu_index {
  z-index: auto !important;
}

.dp__overlay {
  z-index: auto !important;
}

.dp__overlay_cell {
  z-index: auto !important;
}

.dp__calendar_header {
  background-color: $icon-bg !important;
  color: $icon-text !important;

  button {
    color: $icon-text !important;

    &:hover {
      background-color: $hover-bg !important;
    }
  }
}

.dp__calendar {
  background-color: $primary-bg !important;
}

.dp__calendar_row {
  background-color: $primary-bg !important;
}

.dp__cell_inner {
  color: $primary-text !important;
  background-color: transparent !important;

  &:hover {
    background-color: $icon-bg !important;
    color: $icon-text !important;
  }
}

.dp__active_date {
  background-color: $icon-bg !important;
  color: $icon-text !important;
}

.dp__today {
  border-color: $border-color !important;
}

.dp__month_year_row {
  background-color: $icon-bg !important;
  color: $icon-text !important;
}

.dp__month_year_select {
  background-color: $primary-bg !important;
  color: $primary-text !important;
  border: 1px solid $border-color !important;

  &:focus {
    @include focus-state;
  }
}

/* Save Button States */
.button {
  &--saving {
    background-color: $icon-bg !important;
    color: $icon-text !important;
    cursor: not-allowed !important;
    opacity: 0.8;
  }
}

/* Error Styling */
.error-message {
  font-size: var(--font-size-xs);
  margin-top: var(--spacing-xs);
  margin-bottom: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 3px;
  background-color: rgba(220, 53, 69, 0.1);
  border-left: 3px solid var(--error-color);
}

/* Hint text styling */
.text-muted.small {
  font-size: var(--font-size-xs);
  margin-top: var(--spacing-xs);
  color: var(--text-secondary);
  opacity: 0.8;
}

/* VueDatePicker disabled dates styling */
.dp__cell_inner.dp__cell_disabled {
  filter: saturate(0.3) !important;
  cursor: not-allowed !important;
  text-decoration: line-through !important;
}

.dp__cell_inner.dp__cell_disabled:hover {
  filter: saturate(0.3) !important;
}

/* Additional styling for disabled dates to make them more obvious */
.dp__calendar_row .dp__cell_inner.dp__cell_disabled {
  position: relative;
}

.dp__calendar_row .dp__cell_inner.dp__cell_disabled::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 1px;
  background-color: rgba($primary-text, 0.3);
  transform: translate(-50%, -50%) rotate(-45deg);
  pointer-events: none;
}

/* Also target past dates that are disabled */
.dp__cell_inner.dp--past {
  filter: saturate(0.3) !important;
  cursor: not-allowed !important;
}

.dp__cell_inner.dp--past:hover {
  filter: saturate(0.3) !important;
}

.general-error {
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid var(--error-color);
  border-radius: var(--border-radius-sm);
}

/* Invalid input styling */
.form-control.is-invalid {
  border-color: var(--error-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.dp__input.is-invalid {
  border-color: var(--error-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

/* Avatar input styling */
.avatar-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-input-wrapper {
  position: relative;
  flex: 1;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 1;
}

.file-input-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: 10px var(--spacing-lg);
  background: var(--gradient-primary);
  color: var(--light-base);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-weight: bold;
  font-size: var(--font-size-sm);
  transition: var(--transition-normal);
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 0;

  &:hover {
    background: var(--gradient-primary-hover);
    border-color: var(--hover-bg);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px var(--shadow-light);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px var(--shadow-light);
  }
}

.file-input-text {
  flex: 1;
  text-align: center;
}

.file-input-icon {
  font-size: 16px;
  opacity: 0.8;
}

.current-avatar-preview {
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Crop modal styling for events */
#event-crop-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

#event-crop-area {
  max-width: 100%;
  max-height: 100%;
  position: relative;

  img {
    max-width: 600px;
    max-height: 400px;
    width: auto;
    height: auto;
    display: block;
  }
}

label.form-title {
  color: var(--light-base);
}

/* Tiptap Editor Styles */
.tiptap-editor-wrapper {
  width: 100%;
  background-color: $primary-bg !important;
  color: $primary-text !important;
  border: 1px solid $border-color;
  border-radius: 4px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.tiptap-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid $border-color;
  background-color: $icon-bg;
  flex-wrap: wrap;
}

.toolbar-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid $border-color;
  border-radius: 4px;
  background-color: $primary-bg;
  color: $primary-text;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    background-color: $hover-bg;
    border-color: $hover-bg;
  }

  &.is-active {
    background-color: $icon-bg;
    color: $icon-text;
    border-color: $icon-bg;
  }

  i {
    font-size: 16px;
  }
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background-color: $border-color;
  margin: 0 4px;
}

.tiptap-editor-content {
  flex: 1;
  padding: 12px;
  min-height: 150px;
  overflow-y: auto;
  position: relative;
}

.tiptap-editor-content :deep(.ProseMirror) {
  outline: none;
  min-height: 150px;
  color: $primary-text !important;
  width: 100%;
  cursor: text;

  &:focus {
    outline: none;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: $primary-text;
    opacity: 0.5;
    pointer-events: none;
    height: 0;
  }
}

.tiptap-editor-wrapper :deep(.ProseMirror p) {
  margin: 0.5em 0;
}


.tiptap-editor-wrapper :deep(.ProseMirror h1),
.tiptap-editor-wrapper :deep(.ProseMirror h2),
.tiptap-editor-wrapper :deep(.ProseMirror h3) {
  margin: 0.5em 0;
  font-weight: bold;
}

.tiptap-editor-wrapper :deep(.ProseMirror ul),
.tiptap-editor-wrapper :deep(.ProseMirror ol) {
  padding-left: 1.5em;
  margin: 0.5em 0;
}

.tiptap-editor-wrapper :deep(.ProseMirror strong) {
  font-weight: bold;
}

.tiptap-editor-wrapper :deep(.ProseMirror em) {
  font-style: italic;
}

.tiptap-editor-wrapper :deep(.ProseMirror code) {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
}

.tiptap-editor-wrapper :deep(.ProseMirror blockquote) {
  border-left: 3px solid $border-color;
  padding-left: 1em;
  margin: 0.5em 0;
  font-style: italic;
}
</style>
